import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, once } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { Direction } from 'webapp/enums/enums';

import didUpdate from 'webapp/modifiers/did-update';
import FormRangeNullableComponent from 'webapp/components/form/range/nullable';

import type BoardState from 'webapp/models-custom/board-state';
import DateRange from 'webapp/models-custom/date-range';
import DsCompare from 'webapp/models-custom/ds-compare';
import MetricLimitations from 'webapp/models-custom/metric-limitations';

interface Args {
	boardState: BoardState;
	slideCount?: number;
	onChange: (
		dateRange: DateRange | null,
		compare: DsCompare | null,
		applyToWholeLoop?: boolean,
	) => void;
	size?: string;
	theme?: string;
	isDisabled?: boolean;
	openAllBlockPreviewProps?: boolean;
}

export default class BoardMasterDateRangeSwitchComponent extends Component<Args> {
	@tracked hasSelection = false;
	@tracked limitations!: MetricLimitations;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.boardStateChanged();
	}

	get shownMasterDateRange() {
		return (
			this.args.boardState.selectedMasterDateRange ||
			this.args.boardState.commonMasterDateRange
		);
	}

	get shownMasterCompare() {
		return this.args.boardState.selectedMasterCompare;
	}

	get showFooterCheckboxLabel(): boolean {
		const slideCount = this.args.slideCount;
		return !!slideCount && slideCount > 1;
	}

	get footerCheckboxLabel() {
		return `Apply to whole Loop (${this.args.slideCount})`;
	}

	get popupDirection() {
		return [Direction.UP, Direction.DOWN];
	}

	private createDateRange() {
		let dateRange;
		const shownMasterDateRange = this.shownMasterDateRange;
		if (this.hasSelection && shownMasterDateRange) {
			dateRange = shownMasterDateRange.createCopy();
		} else {
			dateRange = new DateRange();
		}
		return dateRange;
	}

	private createCompare(dateRange: DateRange) {
		let compare;
		const shownCompare = this.shownMasterCompare;
		if (shownCompare) {
			compare = shownCompare.createCopy();
		} else {
			compare = DsCompare.createFromFormula(null, dateRange);
		}
		compare.customNoneText = 'No changes';
		return compare;
	}

	private resetLimitations() {
		const dateRange = this.createDateRange();
		const limitations = new MetricLimitations({
			metrics: this.args.boardState.metricData,
			dateRange: dateRange,
			compareCMs: [this.createCompare(dateRange)],
		});

		this.limitations = limitations;
	}

	@action
	boardStateChanged() {
		this.hasSelection = !!this.shownMasterDateRange;
		this.resetLimitations();
	}

	@action
	updateLimitationsFromMetricData() {
		// next, to trigger only after all blocks have been added to the boardState
		next(() => {
			this.limitations?.changeMetrics(this.args.boardState.metricData);
		});
	}

	@action
	updateDateRange(
		dateRange: DateRange | null,
		compare?: DsCompare | null,
		applyToWholeLoop?: boolean,
	) {
		this.hasSelection = !!dateRange;

		once(this, this.args.onChange, dateRange, compare || null, applyToWholeLoop);

		if (!dateRange && !this.limitations?.dateRange?.isEmpty) {
			this.resetLimitations(); // bcos we want clean slate when range picker is opened
		}
	}
	<template>
		<div
			class="master-date-range"
			{{didUpdate
				this.boardStateChanged
				@boardState.selectedMasterDateRange
				@boardState.commonMasterDateRange
			}}
			{{didUpdate this.updateLimitationsFromMetricData @boardState.metricData}}
		>

			<FormRangeNullableComponent
				@metricLimitations={{this.limitations}}
				@hasSelection={{this.hasSelection}}
				@onChange={{this.updateDateRange}}
				@theme={{@theme}}
				@size={{@size}}
				@isDisabled={{@isDisabled}}
				@openAllBlockPreviewProps={{@openAllBlockPreviewProps}}
				@collapseGranulation={{true}}
				@footerCheckboxLabel={{if this.showFooterCheckboxLabel this.footerCheckboxLabel}}
				@popupDirection={{this.popupDirection}}
				@showMasterCompare={{true}}
				@trackingOrigin="master"
			/>

		</div>
	</template>
}
